import axios from 'axios';

import { handleAuthExpiry } from '../utils/authExpiry';

import { API_CONFIG } from './config';

// Interface for token data
interface TokenData {
  accessToken: string | null;
  accessExpiry: string | null;
  refreshToken: string | null;
  refreshExpiry: string | null;
}

/**
 * Get authentication tokens from localStorage
 */
export function getTokens(): TokenData {
  return {
    accessToken: localStorage.getItem('access-token'),
    accessExpiry: localStorage.getItem('access-expiry'),
    refreshToken: localStorage.getItem('refresh-token'),
    refreshExpiry: localStorage.getItem('refresh-expiry'),
  };
}

/**
 * Store authentication tokens in localStorage
 */
export function setTokens(
  accessToken: string,
  accessExpiry: string,
  refreshToken: string,
  refreshExpiry: string,
): void {
  localStorage.setItem('access-token', accessToken);
  localStorage.setItem('access-expiry', accessExpiry);
  localStorage.setItem('refresh-token', refreshToken);
  localStorage.setItem('refresh-expiry', refreshExpiry);
}

/**
 * Clear all authentication tokens from localStorage
 */
export function clearTokens(): void {
  ['access-token', 'access-expiry', 'refresh-token', 'refresh-expiry'].forEach(
    (key) => localStorage.removeItem(key),
  );
}

/**
 * Extract expiration time from a JWT token
 * @param token JWT token
 * @returns ISO date string of expiration time
 */
function getTokenExpiration(token: string): string {
  try {
    // Extract the payload
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    // Convert base64url to base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    // Decode the payload
    const jsonStr = atob(paddedBase64);
    const payload = JSON.parse(jsonStr) as { exp?: number };

    // Get expiration time
    if (typeof payload.exp !== 'number') {
      throw new Error('JWT does not contain expiration claim');
    }

    // Convert to ISO string
    return new Date(payload.exp * 1000).toISOString();
  } catch {
    // Failed to extract JWT expiration
    // Fallback: 1 hour from now
    const fallback = new Date();
    fallback.setHours(fallback.getHours() + 1);
    return fallback.toISOString();
  }
}

/**
 * Refresh the access token using the refresh token
 * Uses a direct axios call to avoid circular dependencies
 */
let refreshPromise: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  // If already refreshing, return the existing promise
  if (refreshPromise) {
    return refreshPromise;
  }

  const {refreshToken} = getTokens();
  if (!refreshToken) {
    clearTokens();
    throw new Error('No refresh token available');
  }

  // Create a promise
  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        { refresh: refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const { access, refresh } = response.data as { access: string; refresh: string };
      const accessExpiry = getTokenExpiration(access);
      const refreshExpiry = getTokenExpiration(refresh);

      setTokens(access, accessExpiry, refresh, refreshExpiry);
      return access;
    } catch (error) {
      clearTokens();
      handleAuthExpiry('refresh-access-token', error instanceof Error ? error : undefined);
      throw error;
    } finally {
      // Reset refreshing state
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Check if the user is currently authenticated
 * by checking if access token exists and is not expired
 */
export function isAuthenticated(): boolean {
  const { accessToken, accessExpiry, refreshToken, refreshExpiry } =
    getTokens();

  // Check if access token is valid
  if (accessToken && accessExpiry) {
    const expiryDate = new Date(accessExpiry);
    const now = new Date();
    if (expiryDate > now) {
      return true;
    }
  }

  // If access token is expired, check refresh token
  if (refreshToken && refreshExpiry) {
    const refreshExpiryDate = new Date(refreshExpiry);
    const now = new Date();
    return refreshExpiryDate > now;
  }

  return false;
}
